import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import TotpSetupStep from './TotpSetupStep';
import { wasRecentlySynced, recordNextAttempt, clearGatewayAttempts, MAX_NEXT_ATTEMPTS } from '../auth/sessionSyncMarker';
import { isWebauthnSupported } from '../auth/webauthnHelpers';
import { navigateAway, reloadPage } from '../lib/tabCloseSuppression';
import './loginNeonRing.css';

// Confirmed against a live Authentik instance on 2026-07-09.
// Stage shapes:
//   ak-stage-prompt                -> raw.fields: [{field_key, label, type, placeholder, required}, ...]
//                                      submit: { [field_key]: value, ... }
//   ak-stage-password              -> raw.pending_user, raw.pending_user_avatar
//                                      submit: { password: value }
//   ak-stage-authenticator-validate -> raw.device_challenges: [{device_class, device_uid, challenge, last_used}]
//                                      submit: { selected_challenge: device_challenges[0], code: value }
//
// On flow completion, the backend intercepts Authentik's `xak-flow-redirect`
// and returns { done: true, token } instead of forwarding a stage.
//
// UPDATED (2026-09-17): device_challenges can now genuinely contain more
// than one entry — until now this array only ever held a single TOTP
// device (confirmed via this file's own history: every submit used to
// blindly index device_challenges[0]), but a user with a passkey enrolled
// (see SetupPasskey.jsx) has BOTH a totp and a webauthn entry here. Find
// the right one by `device_class` explicitly wherever this array is read
// — see findDeviceChallenge below.

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4001/api';
// NOT a trailing slash — same nginx `alias` gotcha as
// SetupPasskey.jsx/accountUpdate.js's enrollment ceremony (see
// authentik-webauthn-related-origins-ingress.yaml's own comment). Keep in
// sync with that file's Ingress path.
const LOGIN_WEBAUTHN_CEREMONY_URL = process.env.REACT_APP_WEBAUTHN_LOGIN_CEREMONY_URL || 'https://auth.bescomapps.com/webauthn-login-ceremony';
// Same ceremony page SetupPasskey.jsx's Profile-panel entry point already
// uses (see accountUpdate.js) — reached a different way here (login.js's
// /start-webauthn-enrollment bootstraps a session for the still-pending
// login instead of requiring an already-completed one), but it's the same
// page either way. redirect=/ tells it to land back at the login page
// (resuming this same in-progress flow) instead of its default /dashboard
// — see webauthn-ceremony.html's own getRedirectUrl().
const WEBAUTHN_ENROLLMENT_CEREMONY_URL = process.env.REACT_APP_WEBAUTHN_CEREMONY_URL || 'https://auth.bescomapps.com/webauthn-ceremony';

// See the doc comment above — falls back to [0] (the old, pre-passkey
// behavior) if the requested class genuinely isn't present, so a TOTP-only
// account (still the vast majority) sees zero behavior change: there's
// only ever one entry for them, `device_class` on it is 'totp' either way,
// and this returns the exact same thing challenge.raw.device_challenges[0]
// already did.
function findDeviceChallenge(deviceChallenges, deviceClass) {
  if (!Array.isArray(deviceChallenges)) return undefined;
  return deviceChallenges.find((c) => c.device_class === deviceClass) || deviceChallenges[0];
}

// Shared style tokens — same language as CompleteProfile.jsx / the old Login.jsx
const page = {
  minHeight: '100vh',
  boxSizing: 'border-box',
  backgroundImage: 'url(/BESCOM_SSO_BG_Image.png)',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '20px',
};

// Darker, more transparent glass than the admin section's own card — the
// login screen sits on a moody night-time background image and reads
// better letting more of it through, closer to the reference design, than
// the brighter/whiter card used elsewhere. Text colors below (lbl,
// textMuted, textStrong) are all keyed to THIS darker background — they'd
// be unreadable against the lighter admin-page card.
const card = {
  background: 'rgba(10, 20, 40, 0.38)',
  backdropFilter: 'blur(14px)',
  WebkitBackdropFilter: 'blur(14px)',
  border: '1px solid rgba(255, 255, 255, 0.22)',
  borderRadius: '16px',
  padding: '56px 48px',
  width: '100%',
  // FOUND LIVE while debugging the neon ring (2026-10-16): previously
  // maxWidth:'500px' WITHOUT boxSizing:'border-box' below meant the
  // 56px/48px padding was added ON TOP of that limit (the content-box
  // default), so the card actually rendered ~596px wide, ~96px more than
  // its own stated maxWidth — overflowing past anything sized to "match
  // the card" (like the ring wrapper around it) on the right/bottom.
  // Several completely different ring techniques (masking, border-image,
  // plain strips) all independently "failed" on the right edge before
  // this was found — none of them were actually broken; the card's real
  // size just never matched what they assumed. Bumped to 596px here (the
  // card's own actual prior rendered size) so fixing the box-sizing bug
  // below doesn't also shrink the card visually from what users already
  // see today.
  maxWidth: '596px',
  boxSizing: 'border-box',
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.45)',
};

const inp = {
  width: '100%',
  padding: '10px 14px',
  border: '1.5px solid rgba(211, 209, 199, 0.6)',
  borderRadius: '8px',
  fontSize: '17px',
  boxSizing: 'border-box',
  color: '#2c2c2a',
  background: 'rgba(255, 255, 255, 0.55)',
  outline: 'none',
};

const lbl = {
  display: 'block',
  fontSize: '13px',
  fontWeight: '500',
  color: 'rgba(255, 255, 255, 0.85)',
  marginBottom: '5px',
};

// Secondary/muted and emphasized text sitting directly on the card
// background (not inside an input, which keeps its own light background
// and dark text regardless) — kept as named tokens rather than repeating
// the same rgba string everywhere, since every one of these used to be a
// dark gray tuned for the old light card.
const textMuted = 'rgba(255, 255, 255, 0.75)';
const textStrong = '#fff';

const btn = {
  width: '100%',
  padding: '14px',
  background: 'linear-gradient(135deg, #009FE3 0%, #0072B1 100%)',
  color: '#fff',
  border: 'none',
  borderRadius: '10px',
  fontSize: '16px',
  fontWeight: '600',
  cursor: 'pointer',
  marginTop: '8px',
};

const btnDisabled = { ...btn, opacity: 0.6, cursor: 'not-allowed' };

// Rotating neon ring effect around the card — see loginNeonRing.css (now a
// real imported stylesheet, not an inline JSX <style> tag; see that file's
// own header comment for why, and for the full history of earlier failed
// attempts). Purely decorative, applied behind the existing card — none of
// its own markup, fields, or handlers below are touched.

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const justRegistered = location.state?.justRegistered;
  const [challenge, setChallenge] = useState(null);
  const [values, setValues] = useState({});
  const [error, setError] = useState(null);
  // Set alongside `error` when login.js reports the account is inactive
  // because registration was never finished (not a genuine deactivation)
  // — shows a link into ResumeRegistration.jsx instead of leaving the
  // person stuck at "contact your administrator" with no way back in.
  const [incompleteRegistration, setIncompleteRegistration] = useState(false);
  const [loading, setLoading] = useState(true);
  // Set once recordNextAttempt() reports we've already tried the SAME
  // next= target MAX_NEXT_ATTEMPTS times within its short window — see
  // sessionSyncMarker.js's own header comment for the full bug this
  // guards against (a stale "recently synced" marker repeatedly trusted
  // after Authentik's own session was killed out from under it, looping
  // forever between here and an outpost's login-redirect flow).
  const [gatewayStuck, setGatewayStuck] = useState(false);

  const { user, loading: authLoading, refreshUser } = useAuth();

  // AuthContext.js's forced-logout (mount-time check + 30s watchdog) used
  // to just silently redirect here on revocation with zero explanation —
  // fine for a normal "logged out elsewhere" case, but an admin rejecting
  // someone mid-session looked like an unexplained bug with no visible
  // reason. Reads the one-shot reason AuthContext.js stashes right before
  // that redirect, shows the same message login.js's own block-reason
  // check would show on a fresh login attempt, and clears it immediately
  // so a later, unrelated visit here never resurfaces a stale reason.
  useEffect(() => {
    const reason = sessionStorage.getItem('bescom_logout_reason');
    if (!reason) return;
    sessionStorage.removeItem('bescom_logout_reason');
    if (reason === 'rejected') {
      setError('Your registration was rejected. Please contact your administrator.');
    } else if (reason === 'deactivated') {
      setError('This account has been deactivated. Please contact your administrator.');
    } else if (reason === 'session-ended-elsewhere') {
      // Expected, by-design behavior (logging out of ANY connected app
      // ends every app's session — see NEW-APP-INTEGRATION.md) — not a
      // problem, so deliberately calmer wording than the two error cases
      // above even though it reuses the same styled message slot.
      setError('You were signed out because your session ended in another connected app.');
    }
  }, []);

  // Outpost-fronted apps (e.g. dasdashboard) redirect unauthenticated users
  // here via a Redirect Stage with ?next=<original URL>, so that once
  // logged in, the browser can go straight back — Authentik's own session
  // cookie (already established by the login below) then lets that app's
  // outpost complete silently, without ever showing Authentik's native
  // login form. Restricted to *.bescomapps.com to avoid an open redirect.
  // Pure — takes an explicit candidate string rather than reading location.search itself, so it can also validate
  // a value that arrived some OTHER way than the current URL (see webauthn's handoff effect below, which needs
  // exactly that: see this file's own CONFIRMED ROOT CAUSE comment there for why re-deriving from location.search
  // doesn't work for that one case).
  function isSafeNextTarget(next) {
    if (!next) return false;
    try {
      const url = new URL(next);
      return url.protocol === 'https:' && url.hostname.endsWith('.bescomapps.com');
    } catch (e) {
      return false; // malformed, ignore
    }
  }
  function getSafeNextUrl() {
    const next = new URLSearchParams(location.search).get('next');
    return isSafeNextTarget(next) ? next : null;
  }

  // CONFIRMED BUG (2026-09-04): a connected app's own logout (e.g.
  // Transfer's Navbar.js) can kill the shared Authentik session and
  // immediately redirect back here via ?next=, all before this app's own
  // Back-Channel Logout receiver has had time to learn about it and revoke
  // `user` — the "some seconds" of real network propagation delay between
  // Transfer's endSession call and backchannelLogout.js recording the
  // revocation. During that window, `user` here is still truthy AND
  // sessionSyncMarker's marker can still be "recently synced", so
  // syncAndContinue below skipped the real sync and sent the browser
  // straight to nextUrl with an Authentik session that had already been
  // killed moments earlier — landing on Authentik's own raw login page
  // instead of this app's branded one. A connected app that KNOWS it just
  // logged out (and only that app can know this) can set ?force_sync=1 on
  // its own gateway URL to bypass the marker for this one redirect,
  // guaranteeing a real check instead of trusting a marker that can't see
  // an event still in flight.
  function getForceSync() {
    return new URLSearchParams(location.search).get('force_sync') === '1';
  }

  // Set only on the real top-level redirect back from the login-time
  // WebAuthn ceremony page (see handleUseWebauthn/LOGIN_WEBAUTHN_CEREMONY_URL
  // above) — a one-time token this page exchanges for the actual session
  // cookie via /auth/login/finish-webauthn (see the dedicated mount effect
  // below). Checked by the other mount effects too, so they don't race
  // that exchange with their own (irrelevant, in this case) fetchChallenge/
  // navigate('/dashboard') calls.
  function getWebauthnHandoffToken() {
    return new URLSearchParams(location.search).get('webauthn_handoff');
  }

  // CONFIRMED BUG (2026-07-29): this effect exists for someone landing on
  // / with an already-valid session from a PRIOR visit (e.g. bookmarked
  // this page while still logged in) — it's not meant to fire mid-submit.
  // But refreshUser() below (needed so Dashboard/CompleteProfile don't see
  // a stale logged-out `user`) sets AuthContext's `user` while THIS
  // component is still mounted, which trips this same effect — and its
  // hardcoded navigate('/dashboard') can race whatever destination
  // submitStep actually intended (/complete-profile, /change-password),
  // occasionally winning. Guard it against firing during an active
  // submission instead of guessing at destinations here.
  const justSubmittedRef = useRef(false);

  useEffect(() => {
    if (justSubmittedRef.current) return;
    // See the dedicated webauthn_handoff effect below — that one owns
    // navigation while a handoff exchange is in flight; this generic
    // fallback would otherwise race it to /dashboard using a stale `user`.
    if (getWebauthnHandoffToken()) return;
    // Deliberately does NOT honor ?next= here — see syncAndContinue below,
    // which is what actually handles someone arriving via ?next= while
    // already logged into bescomsso. Only fall back to /dashboard once
    // truly done, with no next= pending.
    if (!authLoading && user && !getSafeNextUrl()) {
      navigate('/dashboard');
    }
  }, [authLoading, user]);

  // CONFIRMED BUG (2026-08-11): no one-shot guard here, unlike every
  // other page in this same gateway chain (SsoRedirect.js/SsoComplete.js/
  // SsoSyncCallback.js all use a triggered.current ref for exactly this
  // reason). `user` can update more than once during mount (a new object
  // reference even when the underlying data hasn't changed still
  // re-triggers a [user]-dependent effect), which fired syncAndContinue
  // twice — two separate /session-sync-link calls, two different one-time
  // recovery tokens, two overlapping navigations racing each other
  // (confirmed via two distinct flow_token/code values landing back to
  // back for what was a single app-card click).
  const syncTriggeredRef = useRef(false);

  useEffect(() => {
    if (authLoading) return;
    // Same reasoning as the effect above — defer entirely to the
    // webauthn_handoff effect while that exchange is in flight.
    if (getWebauthnHandoffToken()) return;
    const nextUrl = getSafeNextUrl();
    // Already logged into bescomsso (e.g. clicked an app card from an
    // active Dashboard session, in a new tab) — no need to make them type
    // their password in again just to reach `next`. Still genuinely
    // refreshes Authentik's own session (via the same sync-link Dashboard.
    // jsx's own lazy sync already uses), so this doesn't reintroduce the
    // "Authentik still sees not authenticated" loop a plain redirect
    // straight to `next` caused (see the effect above's history) — it
    // just skips the redundant credential-collection UI on top of that.
    // CONFIRMED BUG (2026-09-04): syncAndContinue's real sync path (below)
    // isn't a verification step — it's Authentik's recovery-token
    // mechanism, which UNCONDITIONALLY (re-)establishes a session
    // regardless of whether one currently exists. force_sync=1 (see
    // getForceSync() above) is only ever set by a connected app's own
    // post-logout redirect specifically BECAUSE it just killed the
    // Authentik session — routing that through syncAndContinue silently
    // logged the user right back into Authentik seconds after their own
    // logout, undoing it. Whenever force_sync is set, skip syncAndContinue
    // entirely and fall through to a real interactive challenge instead —
    // the one thing that can't silently re-authenticate anyone.
    if (user && nextUrl && !getForceSync()) {
      if (syncTriggeredRef.current) return;
      syncTriggeredRef.current = true;
      syncAndContinue(nextUrl);
      return;
    }
    // Also force a fresh challenge for a logged-out user, one arriving via
    // ?next= with no existing bescomsso session at all, or one that just
    // came from a connected app's own logout (force_sync).
    if (!user || nextUrl) {
      fetchChallenge();
    }
  }, [authLoading, user]);

  // Cheap liveness check reusing the EXISTING /refresh-token route (the
  // same one AuthContext.js's refreshSessionCookie already calls) —
  // confirms this portal's own STORED Authentik session for the user is
  // genuinely still alive, rather than just trusting wasRecentlySynced()'s
  // time-based marker. Deliberately NOT session-sync-link (which mints a
  // fresh Authentik grant and risks the exact same grant-supersession bug
  // the marker exists to avoid) — this only reads current user info via
  // the already-stored cookie, then remints this app's own JWT as a side
  // effect; no new Authentik grant involved either way.
  async function isAuthentikSessionStillAlive() {
    try {
      const res = await fetch(`${API_BASE}/account/refresh-token`, {
        method: 'POST',
        credentials: 'include',
      });
      return res.ok;
    } catch (e) {
      // Network blip — fail CLOSED (treat as "can't confirm this is
      // alive"), not open. Wrongly forcing one extra real sync on an
      // otherwise-healthy connection is a minor cost; wrongly trusting a
      // session that's actually dead is the exact bug this exists to fix.
      return false;
    }
  }

  async function syncAndContinue(nextUrl) {
    // CONFIRMED BUG (2026-09-08) — see sessionSyncMarker.js's own header
    // comment. If we've already tried the fast "trust the marker" path
    // below for this exact `next` target recently and landed BACK on this
    // gateway again, the marker was wrong — force a real sync this time
    // instead of repeating the same mistake. A hard cap avoids looping
    // forever if even a real sync can't resolve it.
    const attempt = recordNextAttempt(nextUrl);
    if (attempt > MAX_NEXT_ATTEMPTS) {
      setGatewayStuck(true);
      setLoading(false);
      return;
    }

    // CONFIRMED ROOT CAUSE (2026-09-03) — see sessionSyncMarker.js. Every
    // sync round trip below mints a NEW Authentik grant for this app's own
    // client, and a second grant superseding an earlier one for the same
    // user fires a real Back-Channel Logout notification that this app's
    // receiver mishandles as a blanket revocation — logging the browser
    // back out within seconds. This is exactly what happened here: another
    // connected app's own gateway redirect (?next=...) landing on an
    // already-logged-in browser doesn't need a fresh grant at all — the
    // live Authentik session cookie is already sufficient to satisfy
    // `nextUrl`'s own redirect_uri allowlist. Skip the round trip entirely
    // when one already succeeded recently AND this is genuinely the first
    // attempt at this target (see the loop guard above otherwise).
    //
    // force_sync=1 never reaches this function at all (see getForceSync()
    // above and the caller effect) — this mints a session unconditionally
    // via a recovery token, which is never safe to run right after a
    // connected app's own logout.
    //
    // CONFIRMED BUG (2026-09-11): trusting the marker on its own wasn't
    // enough — it only tracks whether a sync succeeded recently, not
    // whether the underlying Authentik session is STILL alive right now.
    // A session can die through a path this app is never notified about
    // at all (e.g. a connected app ending every Authentik session via a
    // direct admin-API call, rather than this app's centralized
    // /api/internal/end-session flow — that doesn't trigger Back-Channel
    // Logout, so nothing here ever learns the session is gone). The
    // marker then stayed wrongly "trusted" for its full window, jumping
    // straight to nextUrl's real, no-more-fallbacks signinRedirect() —
    // which, with no live session, lands on Authentik's own raw
    // interactive login instead of bouncing back with anything
    // detectable. Confirmed live via zero backchannelLogout log entries
    // and an unset revocation key despite the session genuinely being
    // dead. isAuthentikSessionStillAlive() closes that gap with one
    // cheap, read-only-in-effect check before trusting the marker.
    if (wasRecentlySynced() && attempt === 1) {
      const stillAlive = await isAuthentikSessionStillAlive();
      if (stillAlive) {
        navigateAway(nextUrl);
        return;
      }
      // Falls through to the real sync round trip below, same as if the
      // marker had never been set — this is what actually recovers a
      // session that died through an unnotified path.
    }
    // Phase 2 of the httpOnly-cookie migration (see AuthContext.js's own
    // header comment) — only called when `user` is already truthy (i.e.
    // GET /account/me already succeeded), so a real session is already
    // about as confirmed as it can be; no separate pre-check needed. Any
    // failure below still falls through to fetchChallenge(), same as before.
    try {
      const res = await fetch(`${API_BASE}/account/session-sync-link`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      if (data.sessionSyncUrl) {
        localStorage.setItem('bescom_pending_next', nextUrl);
        navigateAway(data.sessionSyncUrl);
        return;
      }
    } catch (e) {
      // fall through to the interactive challenge below
    }
    // No sync link available (e.g. AUTHENTIK_API_TOKEN unset server-side,
    // or the request failed) — fall back to a normal interactive login
    // rather than stranding the user on a blank page.
    fetchChallenge();
  }

  async function fetchChallenge() {
    setLoading(true);
    setError(null);
    const attempt = async () => {
      const res = await fetch(`${API_BASE}/auth/login/challenge`, { credentials: 'include' });
      return res.json();
    };
    try {
      let data;
      try {
        data = await attempt();
      } catch (e) {
        // FIXED 2026-09-29: this runs on mount whenever a logged-out user
        // lands here (see the effect above) — including a long-idle tab
        // being resumed, whose very first request often lands on a stale/
        // dropped connection (a proxy or the browser itself reaping an
        // idle keep-alive), not a real outage. That used to strand the
        // user on this dead-end error with no way back except a manual
        // page reload. One quick, silent retry self-heals the common case
        // without needing to know the exact underlying network cause; if
        // it fails twice in a row in under a second, that's a genuinely
        // real problem worth surfacing.
        await new Promise((resolve) => setTimeout(resolve, 800));
        data = await attempt();
      }
      setChallenge(data);
      setValues({});
    } catch (e) {
      setError('Could not reach the login service.');
    } finally {
      setLoading(false);
    }
  }

  // ADDED (2026-09-17): catches the real top-level redirect back from the
  // login-time WebAuthn ceremony page (see handleUseWebauthn above) —
  // exchanges the one-time handoff token for the actual bescom_token
  // session cookie (same-origin here, unlike the ceremony page's own
  // cross-origin calls, so Set-Cookie actually works), then feeds the
  // response into processStepResult — the SAME completion logic a normal
  // TOTP submission uses (mustChangePassword, sessionSyncUrl, etc.), so
  // this doesn't duplicate any of that already-hardened branching.
  // handoffTriggeredRef: React 18 Strict Mode double-invokes effects in
  // dev, and a handoff token is single-use server-side — without this,
  // the second invocation would always see it already consumed and show a
  // spurious "expired" error even on a genuinely successful sign-in.
  const handoffTriggeredRef = useRef(false);

  useEffect(() => {
    const handoffToken = getWebauthnHandoffToken();
    if (!handoffToken || handoffTriggeredRef.current) return;
    handoffTriggeredRef.current = true;
    justSubmittedRef.current = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${API_BASE}/auth/login/finish-webauthn`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ handoffToken }),
        });
        const data = await res.json();
        if (!res.ok || !data.done) {
          setError(data.error || 'Your passkey sign-in link has expired. Please try again.');
          setLoading(false);
          fetchChallenge();
          return;
        }
        // CONFIRMED BUG (2026-09-25, found integrating coi-plan behind an outpost): navigating away to the
        // cross-origin ceremony page and back replaces this page's whole query string with just
        // ?webauthn_handoff=..., dropping whatever ?next= originally got us here. Fixed by having
        // handleUseWebauthn (below) forward next through start-webauthn-ceremony -> Redis -> the handoff
        // response, so it comes back here as data.next.
        //
        // CONFIRMED ROOT CAUSE of why "restore it into the URL first" (two earlier attempts: raw
        // history.replaceState(), then navigate()) still didn't work: this whole effect runs from a closure
        // frozen at the component's FIRST render — useEffect(..., []) never swaps in a newer callback once
        // mounted — so the getSafeNextUrl()/processStepResult references it holds are permanently bound to
        // whatever `location` (React Router's useLocation(), which every bare `location` in this file resolves
        // to) looked like AT THAT FIRST RENDER, i.e. before ?next= was ever restored. Neither a raw
        // window.history.replaceState() (updates window.location, confirmed live via explicit logging, but
        // useLocation() never sees it) nor navigate() (updates useLocation() for FUTURE renders, but this
        // frozen closure never re-runs to observe it) can reach backward into an already-created closure.
        // Fixed properly: pass data.next straight into processStepResult as an explicit override instead of
        // trying to make getSafeNextUrl() re-derive it from a location this closure can never see fresh.
        await processStepResult(data, isSafeNextTarget(data.next) ? data.next : undefined);
      } catch (e) {
        setError('Could not complete passkey sign-in. Please try again.');
        setLoading(false);
        fetchChallenge();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Bare network call, no state side effects at all — split out of
  // submitStep (below) so submitCombinedIdentifyAndPassword (further
  // below) can chain two of these back-to-back without a React render
  // landing in between them (an intermediate setChallenge/setLoading(false)
  // between the two calls would flash the separate password-only screen
  // for a frame while the second call is in flight over the network).
  async function performStep(body) {
    const res = await fetch(`${API_BASE}/auth/login/step`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return res.json();
  }

  // Everything submitStep used to do with a step's response, now shared
  // with submitCombinedIdentifyAndPassword below — extracted verbatim
  // (not reimplemented) specifically so that combined-form path can never
  // drift from this already-hardened completion logic (mustChangePassword,
  // sessionSyncUrl, the save-password-prompt navigation fix, etc.).
  // `overrideNextUrl`: CONFIRMED ROOT CAUSE (2026-09-25) — the webauthn handoff effect below calls this from a
  // closure frozen at the component's FIRST render (useEffect(..., []) never swaps in a newer callback), so its
  // own getSafeNextUrl() can never see a `next` that only exists in a LATER render's location — no amount of
  // navigate()/replaceState() timing fixes that, since the closure itself is stale, not just the DOM's URL. Every
  // other caller passes nothing here and behaves exactly as before (falls back to getSafeNextUrl() as always).
  async function processStepResult(data, overrideNextUrl) {
      if (data.done) {
        justSubmittedRef.current = true;
        // bescom_token is now set as an httpOnly cookie by the backend's
        // response itself (see AuthContext.js's own header comment) — no
        // client-side storage step needed anymore.
        if (!data.profileComplete) {
          // sessionSyncUrl causes a real top-level navigation (see comment
          // below), which wipes React state — so this has to survive via
          // localStorage (unlike bescom_token, this one's still a plain
          // flag, not a credential, so localStorage is fine for it).
          localStorage.setItem('bescom_needs_profile', '1');
        } else {
          localStorage.removeItem('bescom_needs_profile');
        }

        if (data.mustChangePassword) {
          // An admin-reset password takes priority over everything else
          // here — no dashboard/cross-app access with a password only the
          // admin currently knows. Skip the sessionSyncUrl navigation
          // entirely (don't want to leave this tab before that's settled);
          // fall back to the SAME lazy flag registration.js's own
          // completion already uses, so Dashboard.jsx still sets up
          // cross-app SSO later, once this is actually resolved.
          //
          // CONFIRMED BUG (2026-07-29): deliberately NOT calling
          // refreshUser() here, unlike the other branches below — doing so
          // sets AuthContext's `user`, which re-renders THIS component
          // while it's still mounted and trips the effect right above
          // (`if (!authLoading && user) navigate('/dashboard')`). That
          // competing navigate('/dashboard') raced this one and,
          // apparently, usually won — the person landed straight on the
          // dashboard, never seeing this screen at all. ChangePasswordRequired.jsx
          // doesn't need `user` resolved (it only checks the token), and
          // calls refreshUser() itself once the password is actually changed.
          if (data.sessionSyncUrl) {
            localStorage.setItem('bescom_needs_session_sync', '1');
          }
          navigate('/change-password');
          return;
        }

        if (data.profileComplete) {
          const nextUrl = overrideNextUrl || getSafeNextUrl();
          if (nextUrl) {
            // See sessionSyncMarker.js — a fresh login only needs one real
            // sync round trip; skip a second if this browser already has a
            // recent one (avoids the same grant-supersession/spurious
            // Back-Channel Logout this marker exists to prevent elsewhere).
            // getForceSync(): see its own comment above syncAndContinue.
            // recordNextAttempt/MAX_NEXT_ATTEMPTS: same loop guard as
            // syncAndContinue above — see sessionSyncMarker.js's header
            // comment (2026-09-08) for why trusting this marker blindly on
            // a repeat visit to the same target can loop forever.
            const loginAttempt = recordNextAttempt(nextUrl);
            if (loginAttempt > MAX_NEXT_ATTEMPTS) {
              setGatewayStuck(true);
              return;
            }
            if (wasRecentlySynced() && !getForceSync() && loginAttempt === 1) {
              navigateAway(nextUrl);
              return;
            }
            // Can't jump straight to nextUrl — bescomsso's login talks to
            // Authentik entirely server-side (a Redis-backed cookie jar),
            // so the browser itself never gets Authentik's own session
            // cookie set. Only sessionSyncUrl does that, via a real
            // top-level navigation. Route through it first (stashing
            // nextUrl for SsoSyncCallback.jsx to pick up once that
            // completes) — jumping straight to nextUrl left Authentik
            // still seeing "not authenticated" on the way back, looping
            // its Redirect Stage forever.
            if (data.sessionSyncUrl) {
              localStorage.setItem('bescom_pending_next', nextUrl);
              navigateAway(data.sessionSyncUrl);
              return;
            }
            navigateAway(nextUrl);
            return;
          }
        }

        // UPDATED 2026-07-29: used to eagerly follow sessionSyncUrl here via
        // a real top-level navigation on EVERY login — required because
        // Authentik sends X-Frame-Options: deny, so it can't be done
        // invisibly via an iframe. That worked, but was visibly jarring:
        // the browser flashed through Authentik's own domain on every
        // single login before landing back on /dashboard. Registration
        // solved the exact same tradeoff by deferring this to Dashboard.jsx's
        // own first safe load instead (see accountUpdate.js's
        // /session-sync-link header) — apply the same lazy pattern here,
        // same as the mustChangePassword branch above already does.
        if (data.sessionSyncUrl) {
          localStorage.setItem('bescom_needs_session_sync', '1');
        }
        // skipRefresh: this token was just minted a few lines above — see
        // AuthContext.js's own comment on resolveUser's skipRefresh param.
        await refreshUser({ skipRefresh: true });
        // CONFIRMED (2026-09-10): a plain React Router navigate() here — a
        // client-side transition with no real page load — is very likely
        // why Chrome/Edge's native "Save password?" prompt wasn't
        // appearing even after the identification/password fields got
        // proper autoComplete attributes (see PromptStep/PasswordStep's
        // own comments). Browsers' password-save heuristics for AJAX-
        // driven logins generally need to observe an actual navigation
        // shortly after the password form submits to treat it as "login
        // succeeded" — every OTHER completion branch in this function
        // already uses a real window.location.href redirect (see
        // data.mustChangePassword and data.profileComplete + nextUrl
        // above); this was the one exception.
        navigateAway(data.profileComplete ? '/dashboard' : '/complete-profile');
        return;
      }

      if (data.errors) {
        const firstKey = Object.keys(data.errors)[0];
        setError(data.errors[firstKey]?.[0]?.string || 'Something went wrong. Please try again.');
        setIncompleteRegistration(!!data.incompleteRegistration);
      }

      setChallenge(data);
      setValues({});
  }

  async function submitStep(body) {
    setLoading(true);
    setError(null);
    setIncompleteRegistration(false);
    try {
      const data = await performStep(body);
      await processStepResult(data);
    } catch (e) {
      setError('Could not reach the login service.');
    } finally {
      setLoading(false);
    }
  }

  // Drives Authentik's identification stage immediately followed by its
  // password stage, from ONE on-screen form (see PromptStep below) instead
  // of two separate screens the user has to click through. This exists
  // purely so the browser sees username+password together and can offer to
  // save/autofill both in one shot — Authentik's flow itself is still two
  // real, separate sequential steps under the hood (its identification
  // stage genuinely doesn't accept a password, and vice versa); nothing
  // about the flow's own server-side structure changed, only how many
  // screens the frontend shows for it.
  //
  // Deliberately calls performStep (not submitStep) for both legs — using
  // submitStep here would setChallenge/setLoading(false) after the FIRST
  // leg, which (since fetch #2 has real network latency) could paint the
  // separate password-only screen for a frame before immediately being
  // replaced once the second leg resolves. Going straight through
  // performStep for both, and only running the (identical, shared)
  // processStepResult once at the end, avoids that flash entirely.
  async function submitCombinedIdentifyAndPassword(allValues) {
    const { password, ...identBody } = allValues;
    setLoading(true);
    setError(null);
    setIncompleteRegistration(false);
    try {
      const data1 = await performStep(identBody);
      // Only chain straight into the password leg when the identification
      // leg genuinely succeeded and asked for a password next — anything
      // else (an identification-stage error, or some other stage entirely)
      // has to be shown as-is instead of blindly submitting a password
      // Authentik never asked for.
      if (!data1.done && !data1.errors && data1.stage === 'ak-stage-password' && password) {
        const data2 = await performStep({ password });
        await processStepResult(data2);
      } else {
        await processStepResult(data1);
      }
    } catch (e) {
      setError('Could not reach the login service.');
    } finally {
      setLoading(false);
    }
  }

  // "Lost your authenticator app?" — offered at the TOTP step. Password was
  // already verified moments ago in this same login attempt, so this skips
  // straight to a fresh QR code rather than a separate phone/email
  // re-verification (see login.js's /reset-totp header for the reasoning).
  const [resettingTotp, setResettingTotp] = useState(false);
  const [totpResetChallenge, setTotpResetChallenge] = useState(null);
  const [totpResetLoading, setTotpResetLoading] = useState(false);
  const [totpResetError, setTotpResetError] = useState(null);
  // Success message shown on the (restarted) sign-in form after an authenticator reset.
  const [notice, setNotice] = useState(null);

  // The "Use your Passkey instead" button below is shown directly (not
  // behind a disclosure toggle) — only ever when this account actually has
  // a webauthn entry in device_challenges (i.e. has a passkey enrolled)
  // AND the browser supports WebAuthn at all. TOTP stays the primary path
  // for everyone else, who never see this at all.
  // Separate from the page's own generic `loading` — that one belongs to
  // submitStep's own network round trip; this covers the real WebAuthn
  // browser ceremony (the biometric/PIN/security-key prompt) running
  // BEFORE anything gets submitted, which isn't a network call at all.
  const [webauthnBusy, setWebauthnBusy] = useState(false);

  // REWRITTEN (2026-09-17): this used to call the WebAuthn browser API
  // directly, which fails the same way SetupPasskey.jsx originally did —
  // Authentik's relying-party ID is auth.bescomapps.com, not this app's
  // own origin, so navigator.credentials.get() has to run from a page
  // genuinely hosted there. Mints a one-time token, then hard-navigates to
  // the login ceremony page — which itself redirects back here with
  // ?webauthn_handoff=... once done (see the mount effect below that
  // catches that and feeds it into processStepResult).
  async function handleUseWebauthn() {
    setWebauthnBusy(true);
    setError(null);
    try {
      // Forward ?next= server-side (see the handoff effect above for why) — it can't survive the upcoming
      // cross-origin navigation any other way, since this page's own query string is gone once the ceremony
      // page redirects back with only ?webauthn_handoff=... Re-validated again on the way back out via
      // isSafeNextTarget() before the handoff effect ever passes it to processStepResult — never trusted
      // blindly just because it round-tripped through our own Redis storage.
      const nextUrl = getSafeNextUrl();
      const res = await fetch(`${API_BASE}/auth/login/start-webauthn-ceremony`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(nextUrl ? { next: nextUrl } : {}),
      });
      const data = await res.json();
      if (!data.token) {
        setError('Could not start passkey sign-in. Please try again.');
        setWebauthnBusy(false);
        return;
      }
      navigateAway(`${LOGIN_WEBAUTHN_CEREMONY_URL}?token=${encodeURIComponent(data.token)}`);
    } catch (e) {
      setError('Could not reach the login service.');
      setWebauthnBusy(false);
    }
  }

  // "Don't have a passkey yet? Set one up now" — for accounts with NO
  // webauthn entry in device_challenges yet. Mints a token via
  // /start-webauthn-enrollment (login.js's pending-user bootstrap — see
  // that route's own comment) and redirects to the SAME enrollment
  // ceremony page SetupPasskey.jsx uses, with redirect=/ so it lands back
  // at THIS in-progress login (not /dashboard — enrolling a passkey
  // completes a separate Authentik flow, not this one) once done.
  const [enrollingPasskey, setEnrollingPasskey] = useState(false);

  async function handleStartPasskeyEnrollment() {
    setEnrollingPasskey(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/auth/login/start-webauthn-enrollment`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      if (!data.token) {
        setError(data.error || 'Could not start passkey setup. Please try again.');
        setEnrollingPasskey(false);
        return;
      }
      const redirectBack = encodeURIComponent(window.location.origin + '/');
      navigateAway(`${WEBAUTHN_ENROLLMENT_CEREMONY_URL}?token=${encodeURIComponent(data.token)}&redirect=${redirectBack}`);
    } catch (e) {
      setError('Could not reach the login service.');
      setEnrollingPasskey(false);
    }
  }

  // Code gate in front of "Lost your authenticator app?" and "Set up a passkey
  // now": both are reachable with only a correct password, so a one-time code
  // sent to the account's confirmed email/phone is required first (login.js's
  // /fallback/send + /fallback/verify) — otherwise anyone with the password
  // could swap in their own second factor.
  // { action: 'totp' | 'passkey', stage: 'sending' | 'code', maskedDestination, channel, error, busy, cooldown }
  const [fallback, setFallback] = useState(null);

  // ADDED (2026-09-23): which 2nd-factor method the person picked, when an account has 2+ genuinely available
  // (device_challenges having both totp+webauthn, and/or the account also having one-time-code channel(s) —
  // see login.js's /step, which sets challenge.raw.bescomCodeChannels for a device-holding account that also
  // has at least one). null | 'totp' | 'webauthn' | 'sms' | 'email'. Only meaningful while on
  // ak-stage-authenticator-validate; a single-method account never shows a chooser at all, so this stays null
  // and unused for it — same screen as before. Reset to null on "Not you? Start over" (see that handler) so a
  // fresh sign-in always re-offers the choice rather than remembering a stale pick from a previous attempt in
  // the same tab.
  const [mfaChoice, setMfaChoice] = useState(null);

  // ADDED (2026-11-04): which delivery channel was picked on the
  // 'bescom-code-mfa-choose' stage — a code-only account (no
  // authenticator/passkey device at all) that has BOTH a verified phone
  // and a confirmed email. null | 'sms' | 'email'. Separate from
  // mfaChoice above (that one picks AMONG METHODS — totp/webauthn/code —
  // for a device-holding account; this one picks a CHANNEL within the
  // code method itself, for an account with no device at all).
  const [chosenCodeChannel, setChosenCodeChannel] = useState(null);

  useEffect(() => {
    if (!fallback || fallback.stage !== 'code' || !(fallback.cooldown > 0)) return undefined;
    const t = setTimeout(() => setFallback((f) => (f ? { ...f, cooldown: f.cooldown - 1 } : f)), 1000);
    return () => clearTimeout(t);
  }, [fallback]);

  // `destructive` (2026-09-23): only true for "Lost your authenticator app?" when THIS account genuinely already
  // has a TOTP device to replace — controls whether submitFallbackCode shows the "your current one will be
  // removed" confirm-replace screen afterward. Every other caller (passkey-only's "Use an authenticator app
  // instead", the new code-only screens' "Set one up now" links, "code"/"passkey" actions in general) is a
  // genuine first-time setup with nothing to remove, so this must stay false there — previously hardcoded to
  // always warn about removal regardless, which was simply wrong copy for an account with no device at all.
  async function startFallback(action, destructive = false) {
    setError(null);
    setFallback({ action, destructive, stage: 'sending' });
    try {
      const res = await fetch(`${API_BASE}/auth/login/fallback/send`, { method: 'POST', credentials: 'include' });
      const data = await res.json();
      if (!res.ok) {
        setFallback({ action, destructive, stage: 'code', error: data.error || 'Could not send the code.', unsent: true });
        return;
      }
      setFallback({ action, destructive, stage: 'code', channel: data.channel, maskedDestination: data.maskedDestination, cooldown: 30 });
    } catch (e) {
      setFallback({ action, destructive, stage: 'code', error: 'Could not reach the login service.', unsent: true });
    }
  }

  async function submitFallbackCode(code) {
    setFallback((f) => ({ ...f, busy: true, error: null }));
    try {
      const res = await fetch(`${API_BASE}/auth/login/fallback/verify`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });
      const data = await res.json();
      if (!res.ok) {
        setFallback((f) => ({ ...f, busy: false, error: data.error || 'Incorrect code. Please try again.' }));
        return;
      }
      if (fallback.action === 'totp' && fallback.destructive) {
        // Replacing the authenticator deletes the current one IMMEDIATELY
        // (it has to be gone before a new one can be set up), so say so
        // before doing it — backing out of THIS screen is still safe.
        setFallback({ action: 'totp', destructive: true, stage: 'confirm-replace' });
      } else if (fallback.action === 'totp') {
        // First-time setup (no existing device to warn about removing) — straight through, same as passkey/code.
        setFallback(null);
        handleStartTotpReset();
      } else if (fallback.action === 'code') {
        // Never destructive (only ADDS a code channel — see setup-code-mfa's
        // own comment), so no confirm-replace step, straight to done.
        setFallback(null);
        handleSetUpCodeMfaAtLogin();
      } else {
        setFallback(null);
        handleStartPasskeyEnrollment();
      }
    } catch (e) {
      setFallback((f) => ({ ...f, busy: false, error: 'Could not reach the login service.' }));
    }
  }

  // ADDED (2026-09-23): completes the "Don't have a one-time code option
  // yet? Set one up now" action, after startFallback('code') + the shared
  // code gate above already verified it's really this person. Takes effect
  // from the NEXT sign-in only (see setup-code-mfa's own comment on why) —
  // this login continues normally on whatever method they're already using.
  async function handleSetUpCodeMfaAtLogin() {
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/auth/login/setup-code-mfa`, { method: 'POST', credentials: 'include' });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Could not set up one-time code sign-in.');
        return;
      }
      // Reflects it on THIS screen immediately — we already know everything the chooser needs (the response
      // carries the same fields login.js's /step would have injected), so there's no need to re-fetch or wait
      // for a fresh sign-in. If they're already on the chooser (methodCount >= 2), it now shows 1-2 new options on
      // the very next render; if they're on a single-method screen, going back (or signing in again) shows it.
      // CHANGED (2026-11-04): data.channel/maskedDestination (singular) replaced with data.channels (plural).
      setChallenge((c) => (c ? { ...c, raw: { ...c.raw, bescomCodeChannels: data.channels } } : c));
      setNotice(`One-time code sign-in is set up — you can use it right away.`);
    } catch (e) {
      setError('Could not reach the login service.');
    }
  }

  async function handleStartTotpReset() {
    setResettingTotp(true);
    setTotpResetLoading(true);
    setTotpResetError(null);
    setNotice(null);
    try {
      const res = await fetch(`${API_BASE}/auth/login/reset-totp`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) {
        setTotpResetError(data.error || 'Could not start authenticator reset.');
        return;
      }
      setTotpResetChallenge(data);
    } catch (e) {
      setTotpResetError('Could not reach the login service.');
    } finally {
      setTotpResetLoading(false);
    }
  }

  async function handleConfirmTotpReset({ code }) {
    setTotpResetLoading(true);
    setTotpResetError(null);
    try {
      const res = await fetch(`${API_BASE}/auth/login/reset-totp/confirm`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });
      const data = await res.json();
      if (!res.ok) {
        setTotpResetError(data.error || 'Incorrect code. Please try again.');
        return;
      }
      if (data.done) {
        setTotpResetChallenge(null);
        setValues({});
        // Start the sign-in over instead of continuing the paused one. That
        // flow computed its list of devices BEFORE the reset, so it still
        // offers the deleted authenticator — the new code was rejected as
        // "invalid challenge selected", and a retry fell back to the
        // passkey entry ("No compatible device class"). A fresh flow sees
        // the new device.
        try {
          await fetch(`${API_BASE}/auth/login/cancel`, { method: 'POST', credentials: 'include' });
        } catch (e) {
          // best-effort — fetchChallenge below still asks for a fresh challenge
        }
        setNotice('Authenticator reset. Please sign in again and enter a code from your new authenticator app.');
        // FOUND LIVE (2026-09-23): resettingTotp used to clear BEFORE fetchChallenge()'s response landed — a real
        // window where challenge.stage was still the OLD synthetic stage (e.g. bescom-sms-mfa, for a code-only
        // account setting up an authenticator) but nothing was blocking it from rendering again, so
        // CodeMfaLoginStep briefly re-mounted and its own send-on-mount fired a genuine duplicate OTP. Awaiting
        // here keeps resettingTotp true (showing a plain "Please wait…") until `challenge` has actually been
        // replaced with the fresh, post-cancel one.
        await fetchChallenge();
        setResettingTotp(false);
        return;
      }
      setTotpResetError('Unexpected response from the identity provider. Please try again.');
    } catch (e) {
      setTotpResetError('Could not reach the login service.');
    } finally {
      setTotpResetLoading(false);
    }
  }

  // By the time the QR screen shows, the old authenticator is already
  // deleted (see login.js's /reset-totp), so "cancel" can't put things back —
  // and the paused login flow behind it still lists the deleted device, so
  // dropping back to it is a dead end (any code fails). Restart the sign-in
  // and say what happened instead.
  async function handleCancelTotpReset() {
    setTotpResetChallenge(null);
    setTotpResetError(null);
    setValues({});
    try {
      await fetch(`${API_BASE}/auth/login/cancel`, { method: 'POST', credentials: 'include' });
    } catch (e) {
      // best-effort — fetchChallenge below still asks for a fresh challenge
    }
    setNotice('Your old authenticator app was removed and a new one was not set up. Sign in again to finish setting one up (or use your passkey if you have one).');
    // Same fix as handleConfirmTotpReset above, same reason — keep resettingTotp true until challenge has
    // actually been replaced, so nothing briefly re-mounts on the old stage and fires a duplicate send.
    await fetchChallenge();
    setResettingTotp(false);
  }

  const isUnknownStage =
    challenge && !['ak-stage-prompt', 'ak-stage-password', 'ak-stage-authenticator-validate', 'bescom-sms-mfa', 'bescom-email-mfa', 'bescom-code-mfa-choose'].includes(challenge.stage);

  const [restartAttempt, setRestartAttempt] = useState(0);
  const MAX_AUTO_RESTART_ATTEMPTS = 3;

  useEffect(() => {
    if (!isUnknownStage) {
      setRestartAttempt(0);
      return;
    }
    if (restartAttempt >= MAX_AUTO_RESTART_ATTEMPTS) return;

    const timer = setTimeout(() => {
      setRestartAttempt((n) => n + 1);
      fetchChallenge();
    }, 2500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUnknownStage, restartAttempt]);


  return (
    <div style={page}>
      <div style={{ position: 'relative', width: '100%', maxWidth: '596px' }}>
        <div className="loginNeonGlow" />
        <div className="loginNeonRingStripTop" />
        <div className="loginNeonRingStripBottom" />
        <div className="loginNeonRingStripLeft" />
        <div className="loginNeonRingStripRight" />
        <div className="loginNeonRingCorner loginNeonRingCornerTL" />
        <div className="loginNeonRingCorner loginNeonRingCornerTR" />
        <div className="loginNeonRingCorner loginNeonRingCornerBL" />
        <div className="loginNeonRingCorner loginNeonRingCornerBR" />
        <div style={{ ...card, position: 'relative', zIndex: 2 }}>
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <img src="/bescom-logo-ui.png" alt="BESCOM" style={{ width: '80px', marginBottom: '16px' }} />
          <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#D4A94C', margin: '0 0 8px' }}>
            BESCOM Employee Single Sign-On (SSO) Login
          </h1>
          <p style={{ fontSize: '14px', color: textMuted, margin: 0 }}>
            {resettingTotp ? 'Reset your authenticator app' : 'Access to All BESCOM Applications'}
          </p>
        </div>

        {gatewayStuck && (
          <div style={{ textAlign: 'center' }}>
            <p style={{ color: textMuted, marginBottom: '16px' }}>
              We're having trouble connecting you to that application. This
              can happen if another sign-in was happening at the same
              moment — please try again.
            </p>
            <button
              onClick={() => {
                clearGatewayAttempts();
                setGatewayStuck(false);
                reloadPage();
              }}
              style={btn}
            >
              Try Again
            </button>
          </div>
        )}

        {!gatewayStuck && error && (
          <div style={{
            background: '#FDECEC', color: '#B3261E', padding: '10px 14px',
            borderRadius: '8px', fontSize: '13px', marginBottom: '16px',
          }}>
            {error}
            {incompleteRegistration && (
              <>
                {' '}
                <button
                  type="button"
                  onClick={() => navigate('/resume-registration')}
                  style={{
                    background: 'none', border: 'none', padding: 0,
                    color: '#B3261E', fontWeight: '600', textDecoration: 'underline',
                    cursor: 'pointer', fontSize: '13px',
                  }}
                >
                  Continue Registration
                </button>
              </>
            )}
          </div>
        )}

        {notice && !error && (
          <div style={{
            background: '#E9F7EF', color: '#0F6E56', padding: '10px 14px',
            borderRadius: '8px', fontSize: '13px', marginBottom: '16px', textAlign: 'center',
          }}>
            {notice}
          </div>
        )}

        {justRegistered && !error && (
          <div style={{
            background: '#E9F7EF', color: '#0F6E56', padding: '10px 14px',
            borderRadius: '8px', fontSize: '13px', marginBottom: '16px', textAlign: 'center',
          }}>
            Registration complete — please sign in.
          </div>
        )}

        {loading && !challenge && (
          <p style={{ textAlign: 'center', color: textMuted, fontSize: '14px' }}>Loading…</p>
        )}

        {challenge?.stage === 'ak-stage-prompt' && (
          <PromptStep
            fields={challenge.raw.fields}
            values={values}
            setValues={setValues}
            loading={loading}
            onSubmit={() => submitCombinedIdentifyAndPassword(values)}
          />
        )}

        {challenge?.stage === 'ak-stage-password' && (
          <PasswordStep
            pendingUser={challenge.raw.pending_user}
            avatar={challenge.raw.pending_user_avatar}
            value={values.password || ''}
            setValue={(v) => setValues({ password: v })}
            loading={loading}
            onSubmit={() => submitStep({ password: values.password || '' })}
          />
        )}

        {/* Shown on both the combined identify+password form and the
            separate password-only retry screen it can fall back to (a
            wrong password re-prompts for just the password — see
            submitCombinedIdentifyAndPassword's own comment) — previously
            only shown on the latter, which meant someone who'd genuinely
            forgotten their password wouldn't see this link until AFTER
            typing something wrong once. */}
        {(challenge?.stage === 'ak-stage-prompt' || challenge?.stage === 'ak-stage-password') && (
          <p style={{ textAlign: 'center', fontSize: '13px', marginTop: '12px' }}>
            <a href="/forgot-password" style={{ color: '#009FE3', textDecoration: 'none' }}>
              Forgot password?
            </a>
          </p>
        )}

        {/* Broadened (2026-09-23) to also cover the synthetic bescom-sms-mfa/bescom-email-mfa stages — a
            code-only account (no authenticator/passkey device at all) reaches those directly, never
            ak-stage-authenticator-validate, but can still use this exact same code-gate to set up an
            authenticator or passkey (see the nudges added below each CodeMfaLoginStep). */}
        {['ak-stage-authenticator-validate', 'bescom-sms-mfa', 'bescom-email-mfa', 'bescom-code-mfa-choose'].includes(challenge?.stage) && !resettingTotp && fallback && (
          <FallbackCodeStep
            fallback={fallback}
            onSubmit={submitFallbackCode}
            onResend={() => startFallback(fallback.action, fallback.destructive)}
            onCancel={() => setFallback(null)}
            onConfirmReplace={() => { setFallback(null); handleStartTotpReset(); }}
          />
        )}

        {/* ADDED (2026-11-04): a code-only account (no authenticator/passkey
            device at all) with BOTH a verified phone and a confirmed email —
            previously silently sent to SMS every time (phone-first default),
            now offered a real choice. Once chosenCodeChannel is set, this
            renders the exact same CodeMfaLoginStep the dedicated
            bescom-sms-mfa/bescom-email-mfa stages below already use — that
            component only cares about its `channel` prop, nothing new needed
            there. */}
        {challenge?.stage === 'bescom-code-mfa-choose' && !resettingTotp && !fallback && !enrollingPasskey && (
          chosenCodeChannel ? (
            <>
              <CodeMfaLoginStep
                channel={chosenCodeChannel}
                pendingUser={challenge.raw.pending_user}
                avatar={challenge.raw.pending_user_avatar}
                maskedDestination={chosenCodeChannel === 'sms' ? challenge.raw.maskedPhone : challenge.raw.maskedEmail}
                onVerified={processStepResult}
              />
              {/* FIXED (2026-11-04): this whole stage (code-only account,
                  both channels available) was missing the "don't have an
                  authenticator/passkey yet?" nudges that the single-channel
                  bescom-sms-mfa/bescom-email-mfa stages below already have
                  — a genuinely new account landing here (common: bulk-
                  invited accounts usually have both a phone and an email on
                  file) had no way to set up a device-based method instead,
                  a real regression from before this chooser stage existed. */}
              <p style={{ textAlign: 'center', fontSize: '13px', marginTop: '16px' }}>
                <button type="button" onClick={() => startFallback('totp')} style={{ background: 'none', border: 'none', padding: 0, color: textMuted, textDecoration: 'underline', cursor: 'pointer', font: 'inherit' }}>
                  Don&apos;t have an authenticator app? Set one up now
                </button>
              </p>
              {isWebauthnSupported() && (
                <p style={{ textAlign: 'center', fontSize: '13px', marginTop: '4px' }}>
                  <button
                    type="button"
                    onClick={() => startFallback('passkey')}
                    disabled={enrollingPasskey}
                    style={{ background: 'none', border: 'none', padding: 0, color: textMuted, textDecoration: 'underline', cursor: enrollingPasskey ? 'not-allowed' : 'pointer', font: 'inherit', opacity: enrollingPasskey ? 0.6 : 1 }}
                  >
                    {enrollingPasskey ? 'Starting passkey setup…' : "Don't have a passkey? Set one up now"}
                  </button>
                </p>
              )}
              <p style={{ textAlign: 'center', fontSize: '13px', marginTop: '4px' }}>
                <button
                  type="button"
                  onClick={() => setChosenCodeChannel(null)}
                  style={{ background: 'none', border: 'none', padding: 0, color: textMuted, textDecoration: 'underline', cursor: 'pointer', font: 'inherit' }}
                >
                  ← Choose a different option
                </button>
              </p>
            </>
          ) : (
            <div>
              {challenge.raw.pending_user && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', marginBottom: '20px' }}>
                  {challenge.raw.pending_user_avatar && (
                    <img src={challenge.raw.pending_user_avatar} alt="" width={40} height={40} style={{ borderRadius: '50%' }} />
                  )}
                  <span style={{ fontSize: '14px', fontWeight: '600', color: textStrong }}>{challenge.raw.pending_user}</span>
                </div>
              )}
              <p style={{ textAlign: 'center', color: textStrong, fontSize: '16px', fontWeight: '700', marginBottom: '20px' }}>
                Where should we send your code?
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <button type="button" onClick={() => setChosenCodeChannel('sms')} style={{ ...btn, marginTop: 0, textAlign: 'center' }}>
                  📱 Text message (to {challenge.raw.maskedPhone})
                </button>
                <button type="button" onClick={() => setChosenCodeChannel('email')} style={{ ...btn, marginTop: 0, textAlign: 'center' }}>
                  ✉️ Email (to {challenge.raw.maskedEmail})
                </button>
              </div>
              <p style={{ textAlign: 'center', fontSize: '13px', marginTop: '16px' }}>
                <button type="button" onClick={() => startFallback('totp')} style={{ background: 'none', border: 'none', padding: 0, color: textMuted, textDecoration: 'underline', cursor: 'pointer', font: 'inherit' }}>
                  Don&apos;t have an authenticator app? Set one up now
                </button>
              </p>
              {isWebauthnSupported() && (
                <p style={{ textAlign: 'center', fontSize: '13px', marginTop: '4px' }}>
                  <button
                    type="button"
                    onClick={() => startFallback('passkey')}
                    disabled={enrollingPasskey}
                    style={{ background: 'none', border: 'none', padding: 0, color: textMuted, textDecoration: 'underline', cursor: enrollingPasskey ? 'not-allowed' : 'pointer', font: 'inherit', opacity: enrollingPasskey ? 0.6 : 1 }}
                  >
                    {enrollingPasskey ? 'Starting passkey setup…' : "Don't have a passkey? Set one up now"}
                  </button>
                </p>
              )}
            </div>
          )
        )}

        {challenge?.stage === 'bescom-sms-mfa' && !resettingTotp && !fallback && !enrollingPasskey && (
          <>
            <CodeMfaLoginStep
              channel="sms"
              pendingUser={challenge.raw.pending_user}
              avatar={challenge.raw.pending_user_avatar}
              maskedDestination={challenge.raw.maskedPhone}
              onVerified={processStepResult}
            />
            {/* ADDED (2026-09-23): this account has ONLY a one-time-code method — the mirror image of
                codeSetupNudge above (which offers code to a device-holding account). Same code-gate
                (startFallback -> FallbackCodeStep, broadened above), same setup actions already built for
                the ak-stage-authenticator-validate screen (handleStartTotpReset / handleStartPasskeyEnrollment
                via FallbackCodeStep's onConfirmReplace / submitFallbackCode's 'passkey' branch) — nothing new
                on the backend, both already work off passwordStageCleared, which already recognizes this
                account's underlying ak-stage-access-denied state as "password genuinely verified". */}
            <p style={{ textAlign: 'center', fontSize: '13px', marginTop: '16px' }}>
              <button type="button" onClick={() => startFallback('totp')} style={{ background: 'none', border: 'none', padding: 0, color: textMuted, textDecoration: 'underline', cursor: 'pointer', font: 'inherit' }}>
                Don&apos;t have an authenticator app? Set one up now
              </button>
            </p>
            {isWebauthnSupported() && (
              <p style={{ textAlign: 'center', fontSize: '13px', marginTop: '4px' }}>
                <button
                  type="button"
                  onClick={() => startFallback('passkey')}
                  disabled={enrollingPasskey}
                  style={{ background: 'none', border: 'none', padding: 0, color: textMuted, textDecoration: 'underline', cursor: enrollingPasskey ? 'not-allowed' : 'pointer', font: 'inherit', opacity: enrollingPasskey ? 0.6 : 1 }}
                >
                  {enrollingPasskey ? 'Starting passkey setup…' : "Don't have a passkey? Set one up now"}
                </button>
              </p>
            )}
          </>
        )}

        {challenge?.stage === 'bescom-email-mfa' && !resettingTotp && !fallback && !enrollingPasskey && (
          <>
            <CodeMfaLoginStep
              channel="email"
              pendingUser={challenge.raw.pending_user}
              avatar={challenge.raw.pending_user_avatar}
              maskedDestination={challenge.raw.maskedEmail}
              onVerified={processStepResult}
            />
            <p style={{ textAlign: 'center', fontSize: '13px', marginTop: '16px' }}>
              <button type="button" onClick={() => startFallback('totp')} style={{ background: 'none', border: 'none', padding: 0, color: textMuted, textDecoration: 'underline', cursor: 'pointer', font: 'inherit' }}>
                Don&apos;t have an authenticator app? Set one up now
              </button>
            </p>
            {isWebauthnSupported() && (
              <p style={{ textAlign: 'center', fontSize: '13px', marginTop: '4px' }}>
                <button
                  type="button"
                  onClick={() => startFallback('passkey')}
                  disabled={enrollingPasskey}
                  style={{ background: 'none', border: 'none', padding: 0, color: textMuted, textDecoration: 'underline', cursor: enrollingPasskey ? 'not-allowed' : 'pointer', font: 'inherit', opacity: enrollingPasskey ? 0.6 : 1 }}
                >
                  {enrollingPasskey ? 'Starting passkey setup…' : "Don't have a passkey? Set one up now"}
                </button>
              </p>
            )}
          </>
        )}

        {challenge?.stage === 'ak-stage-authenticator-validate' && !resettingTotp && !fallback && (() => {
          // CONFIRMED live (2026-09-19): device_challenges can genuinely have
          // NO totp entry at all (a passkey-only account, or one whose
          // authenticator was just deleted mid-reset) — findDeviceChallenge's
          // own [0] fallback then hands the webauthn entry to TotpStep's
          // submit, which Authentik rejects with "No compatible device class
          // allowed" the moment any code is typed. Check for a real totp
          // entry explicitly instead of assuming one exists.
          const hasTotp = findDeviceChallenge(challenge.raw.device_challenges, 'totp')?.device_class === 'totp';
          // Raw device presence (browser-independent) vs. actually usable in THIS browser — kept separate on
          // purpose: the passkey-only special case below is shown regardless of browser support (matches the
          // original, unchanged behavior — the only thing there IS to try, even if it might fail), but the new
          // chooser and the "set up a passkey" nudge must not offer something this browser can't do at all.
          const hasWebauthnDevice = findDeviceChallenge(challenge.raw.device_challenges, 'webauthn')?.device_class === 'webauthn';
          const hasWebauthn = hasWebauthnDevice && isWebauthnSupported();
          // ADDED (2026-09-23): set by login.js's /step alongside a real device challenge, when this account ALSO
          // has a one-time-code channel configured (never replacing the device — see accountUpdate.js's
          // /set-code-mfa, already safe to call on a device-holding account). Empty/absent for every account that
          // hasn't opted into this, which is everyone until the new self-service option is used.
          // CHANGED (2026-11-04): bescomCodeChannel (singular) replaced with bescomCodeChannels (plural, one entry
          // per channel this account can actually use) — an account with both a verified phone and a confirmed
          // email now gets 2 separate buttons below instead of being locked onto whichever one login.js used to
          // auto-pick.
          const codeChannels = challenge.raw.bescomCodeChannels || [];
          const smsEntry = codeChannels.find((c) => c.type === 'sms');
          const emailEntry = codeChannels.find((c) => c.type === 'email');
          const hasSms = !!smsEntry;
          const hasEmail = !!emailEntry;
          const hasCode = hasSms || hasEmail;

          const avatarRow = challenge.raw.pending_user && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', marginBottom: '20px' }}>
              {challenge.raw.pending_user_avatar && (
                <img src={challenge.raw.pending_user_avatar} alt="" width={40} height={40} style={{ borderRadius: '50%' }} />
              )}
              <span style={{ fontSize: '14px', fontWeight: '600', color: textStrong }}>{challenge.raw.pending_user}</span>
            </div>
          );

          // ADDED (2026-09-23): offered on whichever method screen they land on (not the chooser itself — that
          // only lists methods that already work), same "Don't have a passkey yet?" placement pattern, same
          // code-gate as "Lost your authenticator app?" (startFallback -> FallbackCodeStep -> a verified code
          // proves it's them, then setup-code-mfa adds the channel). Never shown once the account already has one.
          const codeSetupNudge = !hasCode && (
            <p style={{ textAlign: 'center', fontSize: '13px', marginTop: '8px' }}>
              <button
                type="button"
                onClick={() => startFallback('code')}
                style={{ background: 'none', border: 'none', padding: 0, color: textMuted, textDecoration: 'underline', cursor: 'pointer', font: 'inherit' }}
              >
                Don&apos;t have a one-time code option yet? Set one up now
              </button>
            </p>
          );
          // Same idea, the other two directions — added specifically for the CHOOSER screen (methodCount >= 2):
          // an account there already has SOME 2+ methods but might still be missing one of the other two, and
          // that gap wasn't offered anywhere on that screen before (only codeSetupNudge was). Both are always
          // first-time setups from this screen (methodCount >= 2 here means whichever of these is missing was
          // never configured at all), so `destructive` stays false — matches how the mirrored nudges above the
          // code-only screens already work.
          const authSetupNudge = !hasTotp && (
            <p style={{ textAlign: 'center', fontSize: '13px', marginTop: '8px' }}>
              <button
                type="button"
                onClick={() => startFallback('totp')}
                style={{ background: 'none', border: 'none', padding: 0, color: textMuted, textDecoration: 'underline', cursor: 'pointer', font: 'inherit' }}
              >
                Don&apos;t have an authenticator app? Set one up now
              </button>
            </p>
          );
          const passkeySetupNudge = !hasWebauthn && isWebauthnSupported() && (
            <p style={{ textAlign: 'center', fontSize: '13px', marginTop: '8px' }}>
              <button
                type="button"
                onClick={() => startFallback('passkey')}
                disabled={enrollingPasskey}
                style={{ background: 'none', border: 'none', padding: 0, color: textMuted, textDecoration: 'underline', cursor: enrollingPasskey ? 'not-allowed' : 'pointer', font: 'inherit', opacity: enrollingPasskey ? 0.6 : 1 }}
              >
                {enrollingPasskey ? 'Starting passkey setup…' : "Don't have a passkey? Set one up now"}
              </button>
            </p>
          );

          if (!hasTotp && !hasCode && hasWebauthnDevice) {
            // Passkey-only: no code box to show at all — go straight to the
            // one thing that can actually work, as the primary action.
            // UNCHANGED from before this feature — a genuinely single-method
            // account never sees anything new.
            return (
              <div style={{ textAlign: 'center' }}>
                {avatarRow}
                <button
                  type="button"
                  onClick={handleUseWebauthn}
                  disabled={webauthnBusy || loading}
                  style={(webauthnBusy || loading) ? btnDisabled : btn}
                >
                  {webauthnBusy ? 'Waiting for your device…' : '🔑 Sign in with your Passkey'}
                </button>
                <p style={{ textAlign: 'center', fontSize: '13px', marginTop: '12px' }}>
                  <button
                    type="button"
                    onClick={() => startFallback('totp')}
                    style={{ background: 'none', border: 'none', padding: 0, color: textMuted, textDecoration: 'underline', cursor: 'pointer', font: 'inherit' }}
                  >
                    Use an authenticator app instead
                  </button>
                </p>
                {codeSetupNudge}
              </div>
            );
          }

          // Everything below is reached only when the account has TOTP as (at least) one of its options — the
          // passkey-only case above already returned. `methodCount` uses the browser-gated `hasWebauthn`, so an
          // account with a passkey device but on a browser that can't do WebAuthn degrades to exactly what it
          // showed before this feature existed (methodCount 1, plain TOTP box, no chooser, no passkey mention at
          // all — matching the original `isWebauthnSupported() && (...)` gate around that whole section).
          const methodCount = [hasTotp, hasWebauthn, hasSms, hasEmail].filter(Boolean).length;

          // USER DECISION (2026-09-23): the chooser applies whenever there are 2+ REAL choices, including the
          // existing TOTP+Passkey combination on its own — not just once a code channel is also configured. So an
          // account that already has both today WILL see this new screen the moment this ships, not just accounts
          // that later opt into a one-time code too.
          if (methodCount >= 2 && !mfaChoice) {
            return (
              <div>
                {avatarRow}
                <p style={{ textAlign: 'center', color: textStrong, fontSize: '16px', fontWeight: '700', marginBottom: '20px' }}>
                  Choose how you&apos;d like to sign in
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {hasTotp && (
                    <button type="button" onClick={() => setMfaChoice('totp')} style={{ ...btn, marginTop: 0, textAlign: 'center' }}>
                      🔐 Authenticator app (Microsoft, Google, etc)
                    </button>
                  )}
                  {hasWebauthn && (
                    <button
                      type="button"
                      onClick={() => { setMfaChoice('webauthn'); handleUseWebauthn(); }}
                      disabled={webauthnBusy || loading}
                      style={{ ...((webauthnBusy || loading) ? btnDisabled : btn), marginTop: 0, textAlign: 'center' }}
                    >
                      {webauthnBusy ? 'Waiting for your device…' : '🔑 Passkey (Fingerprint, Pin, Face)'}
                    </button>
                  )}
                  {hasSms && (
                    <button type="button" onClick={() => setMfaChoice('sms')} style={{ ...btn, marginTop: 0, textAlign: 'center' }}>
                      📱 One-time code{smsEntry.masked ? ` (to ${smsEntry.masked})` : ''}
                    </button>
                  )}
                  {hasEmail && (
                    <button type="button" onClick={() => setMfaChoice('email')} style={{ ...btn, marginTop: 0, textAlign: 'center' }}>
                      ✉️ One-time code{emailEntry.masked ? ` (to ${emailEntry.masked})` : ''}
                    </button>
                  )}
                </div>
                {authSetupNudge}
                {passkeySetupNudge}
                {codeSetupNudge}
              </div>
            );
          }

          // A method was chosen from the chooser above, OR there was really only ever one genuine choice (plain
          // TOTP, no usable passkey, no code) — in that single-method case this renders exactly what it always
          // has, with backToChooser evaluating to nothing since methodCount < 2.
          const choice = mfaChoice || (hasTotp ? 'totp' : hasSms ? 'sms' : hasEmail ? 'email' : 'webauthn');
          const backToChooser = methodCount >= 2 && (
            <p style={{ textAlign: 'center', fontSize: '13px', marginTop: '12px' }}>
              <button
                type="button"
                onClick={() => setMfaChoice(null)}
                style={{ background: 'none', border: 'none', padding: 0, color: textMuted, textDecoration: 'underline', cursor: 'pointer', font: 'inherit' }}
              >
                ← Choose a different method
              </button>
            </p>
          );

          if (choice === 'sms' || choice === 'email') {
            return (
              <>
                <CodeMfaLoginStep
                  channel={choice}
                  pendingUser={challenge.raw.pending_user}
                  avatar={challenge.raw.pending_user_avatar}
                  maskedDestination={choice === 'sms' ? smsEntry?.masked : emailEntry?.masked}
                  onVerified={processStepResult}
                />
                {backToChooser}
              </>
            );
          }

          if (choice === 'webauthn') {
            // Reached either straight from the chooser (which already fired handleUseWebauthn on click) or, in
            // principle, a stale mfaChoice — clicking here (re)starts the same real full-page ceremony redirect.
            return (
              <div style={{ textAlign: 'center' }}>
                {avatarRow}
                <button
                  type="button"
                  onClick={handleUseWebauthn}
                  disabled={webauthnBusy || loading}
                  style={(webauthnBusy || loading) ? btnDisabled : btn}
                >
                  {webauthnBusy ? 'Waiting for your device…' : '🔑 Sign in with your Passkey'}
                </button>
                {backToChooser}
                {codeSetupNudge}
              </div>
            );
          }

          // choice === 'totp'
          return (
            <>
              <TotpStep
                pendingUser={challenge.raw.pending_user}
                avatar={challenge.raw.pending_user_avatar}
                value={values.code || ''}
                setValue={(v) => setValues({ code: v })}
                loading={loading}
                onSubmit={() =>
                  submitStep({
                    selected_challenge: findDeviceChallenge(challenge.raw.device_challenges, 'totp'),
                    code: values.code || '',
                  })
                }
              />
              <p style={{ textAlign: 'center', fontSize: '13px', marginTop: '12px' }}>
                <button
                  type="button"
                  onClick={() => startFallback('totp', true)}
                  style={{ background: 'none', border: 'none', padding: 0, color: '#009FE3', textDecoration: 'none', cursor: 'pointer', font: 'inherit' }}
                >
                  Lost your authenticator app?
                </button>
              </p>

              {/* Only reachable when methodCount === 1 (plain TOTP, no usable passkey, no code) — an account
                  with 2+ methods sees backToChooser instead of this nudge, since it already has more than one way
                  in. Condition matches the ORIGINAL pre-chooser behavior exactly: offer to set up a passkey only
                  when the browser supports it and this account doesn't have one yet. */}
              {methodCount === 1 && isWebauthnSupported() && !hasWebauthnDevice && (
                <p style={{ textAlign: 'center', fontSize: '13px', marginTop: '8px' }}>
                  <button
                    type="button"
                    onClick={() => startFallback('passkey')}
                    disabled={enrollingPasskey || loading}
                    style={{
                      background: 'none', border: 'none', padding: 0, color: textMuted, textDecoration: 'underline',
                      cursor: (enrollingPasskey || loading) ? 'not-allowed' : 'pointer', font: 'inherit',
                      opacity: (enrollingPasskey || loading) ? 0.6 : 1,
                    }}
                  >
                    {enrollingPasskey ? 'Starting passkey setup…' : "Don't have a passkey yet? Set one up now"}
                  </button>
                </p>
              )}
              {backToChooser}
              {codeSetupNudge}
            </>
          );
        })()}

        {resettingTotp && (
          <div>
            {totpResetError && (
              <div style={{
                background: '#FDECEC', color: '#B3261E', padding: '10px 14px',
                borderRadius: '8px', fontSize: '13px', marginBottom: '16px',
              }}>
                {totpResetError}
              </div>
            )}
            {totpResetLoading && !totpResetChallenge && (
              <p style={{ textAlign: 'center', color: textMuted, fontSize: '14px' }}>Please wait…</p>
            )}
            {totpResetChallenge?.raw?.config_url && (
              <TotpSetupStep
                configUrl={totpResetChallenge.raw.config_url}
                loading={totpResetLoading}
                onSubmit={handleConfirmTotpReset}
                description="Your old authenticator app has been removed. Scan this code with your authenticator app, then enter the 6-digit code it shows to finish."
                dark
              />
            )}
            <button
              type="button"
              onClick={handleCancelTotpReset}
              style={{ ...btn, background: 'none', border: '1px solid rgba(255, 255, 255, 0.35)', color: textMuted }}
            >
              Cancel
            </button>
          </div>
        )}

        {challenge && ['ak-stage-password', 'ak-stage-authenticator-validate'].includes(challenge.stage) && (
          <p style={{ textAlign: 'center', fontSize: '13px', marginTop: '16px' }}>
            <a
              href="#"
              onClick={async (e) => {
                e.preventDefault();
                setMfaChoice(null);
                await fetch(`${API_BASE}/auth/login/cancel`, {
                  method: 'POST',
                  credentials: 'include',
                });
                fetchChallenge();
              }}
              style={{ color: textMuted, textDecoration: 'underline' }}
            >
              Not you? Start over
            </a>
          </p>
        )}

        {isUnknownStage && restartAttempt < MAX_AUTO_RESTART_ATTEMPTS && (
          <div style={{ textAlign: 'center' }}>
            <p style={{ color: textMuted }}>
              Your session has expired. Restarting…
            </p>
          </div>
        )}

        {isUnknownStage && restartAttempt >= MAX_AUTO_RESTART_ATTEMPTS && (
          <div style={{ textAlign: 'center' }}>
            <p style={{ color: textMuted, marginBottom: '16px' }}>
              We're having trouble reaching the login service. This may be a
              network issue — check your connection and try again.
            </p>
            <button
              onClick={() => {
                setRestartAttempt(0);
                fetchChallenge();
              }}
              style={btn}
            >
              Try Again
            </button>
          </div>
        )}

        {challenge?.stage === 'ak-stage-prompt' && (
          <p className="new-user" style={{ textAlign: 'center', fontSize: '13px', color: textMuted, marginTop: '20px' }}>
            New user?{' '}
            <a href="/register" className="create-account-btn">
              Create an account
            </a>
          </p>
        )}
        </div>
      </div>
    </div >
  );
}

function PromptStep({ fields, values, setValues, loading, onSubmit }) {
  const handleChange = (key, val) => setValues((prev) => ({ ...prev, [key]: val }));
  const minOrder = Math.min(...fields.map((f) => f.order));

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }}>
      {fields.map((field) => (
        <div key={field.field_key} style={{ marginBottom: '16px' }}>
          <label style={lbl}>{field.label}{field.required ? ' *' : ''}</label>
          <input
            style={inp}
            type={field.type === 'email' ? 'email' : 'text'}
            // This component, on this page, only ever renders the login
            // flow's identification stage field(s) — safe to always mark
            // as the username, unlike a generic reusable prompt renderer
            // that might also cover other kinds of fields elsewhere.
            // Lets the browser's own password manager recognize and later
            // autofill this field — see PasswordStep's matching
            // autoComplete="current-password" just below, and this file's
            // own comment on why credentials are never stored by this app
            // itself.
            name="username"
            autoComplete="username"
            placeholder={field.placeholder || ''}
            required={field.required}
            value={values[field.field_key] || ''}
            onChange={(e) => handleChange(field.field_key, e.target.value)}
            autoFocus={field.order === minOrder}
          />
        </div>
      ))}
      {/* Password, collected here alongside the identification field(s)
          above even though Authentik's identification stage itself never
          sees this value (submitCombinedIdentifyAndPassword strips it back
          out before that first leg, then submits it separately as its own
          password-stage leg) — see that function's own comment for why:
          this is what actually gets Chrome/Edge to recognize a real
          username+password pair and offer to save/autofill both together,
          instead of the two-separate-screens version that could only ever
          offer one field at a time. */}
      <div style={{ marginBottom: '16px' }}>
        <label style={lbl}>Password *</label>
        <input
          style={inp}
          type="password"
          name="password"
          autoComplete="current-password"
          required
          value={values.password || ''}
          onChange={(e) => handleChange('password', e.target.value)}
        />
      </div>
      <button type="submit" disabled={loading} style={loading ? btnDisabled : btn}>
        {loading ? 'Please wait…' : 'Continue'}
      </button>
    </form>
  );
}

function PasswordStep({ pendingUser, avatar, value, setValue, loading, onSubmit }) {
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }}>
      {avatar && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
          <img src={avatar} alt="" width={40} height={40} style={{ borderRadius: '50%' }} />
          <span style={{ fontSize: '14px', fontWeight: '600', color: textStrong }}>{pendingUser}</span>
        </div>
      )}
      <div style={{ marginBottom: '16px' }}>
        <label style={lbl}>Password *</label>
        <input
          style={inp}
          type="password"
          // See PromptStep's matching autoComplete="username" above — this
          // is what actually lets the browser offer to save the password
          // and, on a later visit, autofill it (this app never stores or
          // sees a "remembered" password itself, only the browser does).
          name="password"
          autoComplete="current-password"
          required
          value={value}
          onChange={(e) => setValue(e.target.value)}
          autoFocus
        />
      </div>
      <button type="submit" disabled={loading} style={loading ? btnDisabled : btn}>
        {loading ? 'Please wait…' : 'Continue'}
      </button>
    </form>
  );
}

function FallbackCodeStep({ fallback, onSubmit, onResend, onCancel, onConfirmReplace }) {
  const [code, setCode] = useState('');
  const what = fallback.action === 'totp'
    ? (fallback.destructive ? 'reset your authenticator app' : 'set up an authenticator app')
    : fallback.action === 'code' ? 'set up one-time code sign-in' : 'set up a passkey';
  const sending = fallback.stage === 'sending';
  if (fallback.stage === 'confirm-replace') {
    return (
      <div>
        <p style={{ textAlign: 'center', color: textStrong, fontSize: '16px', fontWeight: '600', margin: '0 0 12px' }}>
          Replace your authenticator app?
        </p>
        <p style={{ textAlign: 'center', color: textMuted, fontSize: '14px', lineHeight: 1.5, margin: '0 0 16px' }}>
          Your current authenticator app will be <strong>removed right away</strong>. You then scan a new
          code and enter a code from it to finish — if you leave before that, you won't be able to sign in
          with an authenticator app until you set one up again.
        </p>
        <button type="button" onClick={onConfirmReplace} style={btn}>Yes, replace it</button>
        <button
          type="button"
          onClick={onCancel}
          style={{ ...btn, background: 'none', border: '1px solid rgba(255, 255, 255, 0.35)', color: textMuted }}
        >
          Go back
        </button>
      </div>
    );
  }
  return (
    <div>
      <p style={{ textAlign: 'center', color: textMuted, fontSize: '14px', lineHeight: 1.5, margin: '0 0 16px' }}>
        {sending
          ? 'Sending a code…'
          : fallback.unsent
            ? 'To protect your account, we need to confirm it\'s really you before you can ' + what + '.'
            : `To protect your account, we sent a code to ${fallback.maskedDestination} to confirm it's really you. Enter it to ${what}.`}
      </p>
      {fallback.error && (
        <div style={{
          background: '#FDECEC', color: '#B3261E', padding: '10px 14px',
          borderRadius: '8px', fontSize: '13px', marginBottom: '16px',
        }}>
          {fallback.error}
        </div>
      )}
      {!sending && !fallback.unsent && (
        <form onSubmit={(e) => { e.preventDefault(); onSubmit(code); }}>
          <label style={lbl}>Enter the 6-digit code *</label>
          <input
            style={{ ...inp, textAlign: 'center', fontSize: '20px', letterSpacing: '4px' }}
            type="text"
            inputMode="numeric"
            pattern="[0-9]{6}"
            maxLength={6}
            required
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            autoFocus
          />
          <button type="submit" disabled={fallback.busy || code.length !== 6} style={(fallback.busy || code.length !== 6) ? btnDisabled : btn}>
            {fallback.busy ? 'Verifying…' : 'Verify'}
          </button>
        </form>
      )}
      {!sending && (
        <p style={{ textAlign: 'center', fontSize: '13px', marginTop: '12px' }}>
          <button
            type="button"
            onClick={onResend}
            disabled={fallback.cooldown > 0}
            style={{
              background: 'none', border: 'none', padding: 0, color: '#009FE3', font: 'inherit',
              cursor: fallback.cooldown > 0 ? 'not-allowed' : 'pointer', opacity: fallback.cooldown > 0 ? 0.6 : 1,
            }}
          >
            {fallback.cooldown > 0 ? `Resend code in ${fallback.cooldown}s` : (fallback.unsent ? 'Try again' : 'Resend code')}
          </button>
        </p>
      )}
      <button
        type="button"
        onClick={onCancel}
        style={{ ...btn, background: 'none', border: '1px solid rgba(255, 255, 255, 0.35)', color: textMuted }}
      >
        Cancel
      </button>
    </div>
  );
}

// New second-factor methods (2026-09-20) — see accountUpdate.js's
// /set-sms-mfa + /set-code-mfa and login.js's own header comment on the
// synthetic bescom-sms-mfa / bescom-email-mfa stages for the full design.
// One component for both channels: the only differences are the endpoint
// and which masked destination the server reports. Self-contained like
// PhoneVerifyStep: sends a code on mount, then verifies it — this is the
// account's NORMAL sign-in step here, not a recovery gate (compare
// FallbackCodeStep above, which guards REPLACING a factor).
function CodeMfaLoginStep({ channel, pendingUser, avatar, maskedDestination, onVerified }) {
  const [status, setStatus] = useState('sending'); // sending | sent | verifying
  const [code, setCode] = useState('');
  const [error, setError] = useState(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [destination, setDestination] = useState(maskedDestination || null);

  useEffect(() => {
    sendCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const t = setTimeout(() => setResendCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  async function sendCode() {
    setStatus('sending');
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/auth/login/${channel}-mfa/send`, { method: 'POST', credentials: 'include' });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Could not send the code.');
        setStatus('sent');
        return;
      }
      setDestination(data.maskedPhone || data.maskedEmail);
      setCode('');
      setStatus('sent');
      setResendCooldown(30);
    } catch (e) {
      setError('Could not reach the login service.');
      setStatus('sent');
    }
  }

  async function submitCode(e) {
    e.preventDefault();
    setStatus('verifying');
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/auth/login/${channel}-mfa/verify`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Incorrect code. Please try again.');
        setStatus('sent');
        return;
      }
      await onVerified(data);
    } catch (e) {
      setError('Could not reach the login service.');
      setStatus('sent');
    }
  }

  return (
    <div>
      {pendingUser && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
          {avatar && <img src={avatar} alt="" width={40} height={40} style={{ borderRadius: '50%' }} />}
          <span style={{ fontSize: '14px', fontWeight: '600', color: textStrong }}>{pendingUser}</span>
        </div>
      )}
      <p style={{ textAlign: 'center', color: textMuted, fontSize: '14px', marginBottom: '20px' }}>
        {status === 'sending'
          ? 'Sending a code…'
          : destination
            ? `We've sent a code to ${destination}.`
            : 'Enter the code we sent you.'}
      </p>
      {error && (
        <div style={{ background: '#FDECEC', color: '#B3261E', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '16px' }}>
          {error}
        </div>
      )}
      {status !== 'sending' && (
        <form onSubmit={submitCode}>
          <label style={lbl}>Enter the 6-digit code *</label>
          <input
            style={{ ...inp, textAlign: 'center', fontSize: '20px', letterSpacing: '4px' }}
            type="text"
            inputMode="numeric"
            pattern="[0-9]{6}"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            autoFocus
            required
          />
          <button type="submit" disabled={status === 'verifying' || code.length !== 6} style={(status === 'verifying' || code.length !== 6) ? btnDisabled : btn}>
            {status === 'verifying' ? 'Verifying…' : 'Verify'}
          </button>
          <button
            type="button"
            onClick={sendCode}
            disabled={resendCooldown > 0}
            style={{
              ...btn, background: 'none', border: '1px solid rgba(255, 255, 255, 0.35)', color: textMuted,
              opacity: resendCooldown > 0 ? 0.6 : 1, cursor: resendCooldown > 0 ? 'not-allowed' : 'pointer',
            }}
          >
            {resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : 'Resend Code'}
          </button>
        </form>
      )}
    </div>
  );
}

function TotpStep({ pendingUser, avatar, value, setValue, loading, onSubmit }) {
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }}>
      {pendingUser && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
          {avatar && <img src={avatar} alt="" width={40} height={40} style={{ borderRadius: '50%' }} />}
          <span style={{ fontSize: '14px', fontWeight: '600', color: textStrong }}>{pendingUser}</span>
        </div>
      )}
      <div style={{ marginBottom: '16px' }}>
        <label style={lbl}>Enter the 6-digit code from your authenticator app *</label>
        <input
          style={{ ...inp, textAlign: 'center', fontSize: '20px', letterSpacing: '4px' }}
          type="text"
          inputMode="numeric"
          pattern="[0-9]{6}"
          maxLength={6}
          required
          value={value}
          onChange={(e) => setValue(e.target.value)}
          autoFocus
        />
      </div>
      <button type="submit" disabled={loading || value.length !== 6} style={(loading || value.length !== 6) ? btnDisabled : btn}>
        {loading ? 'Verifying…' : 'Verify'}
      </button>
    </form>
  );
}
